/**
 * Audio waveform visualization component.
 *
 * Displays a row of bars indicating audio playback. When a `getLevels` callback
 * is provided (backed by useAudioAnalyser), the bars reflect the REAL audio
 * amplitude of the playing track — flat during silent passages. When omitted, it
 * falls back to a generic animation driven by the `isPlaying` flag.
 *
 * @module AudioWaveform
 */

import { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';

interface AudioWaveformProps {
  readonly isPlaying: boolean;
  readonly className?: string;
  /**
   * Optional real-audio driver. Fills the provided array with 0–1 magnitudes and
   * returns true if real data is available. When provided, fake animation is
   * disabled and bars show actual audio (flat on silence).
   */
  readonly getLevels?: (out: number[]) => boolean;
}

const TOTAL_BARS = 40;
const MIN_HEIGHT = 20; // % — baseline so idle bars are still visible

// Generate stable bar IDs outside the component to avoid recreating on each render
const MAIN_BARS = Array.from({ length: 20 }, (_, i) => ({ id: `bar-${i}`, index: i }));
const EXTRA_BARS = Array.from({ length: 20 }, (_, i) => ({ id: `bar-extra-${i}`, index: i + 20 }));

export function AudioWaveform({ isPlaying, className, getLevels }: AudioWaveformProps) {
  const barsRef = useRef<HTMLDivElement[]>([]);
  const animationRef = useRef<number>();
  // Reusable buffer so we don't allocate every frame
  const levelsRef = useRef<number[]>(new Array(TOTAL_BARS).fill(0));

  useEffect(() => {
    if (isPlaying) {
      const animate = () => {
        const out = levelsRef.current;
        const hasReal = getLevels ? getLevels(out) : false;

        barsRef.current.forEach((bar, index) => {
          if (!bar) return;
          let height: number;
          if (getLevels) {
            // Real-audio mode: use actual amplitude (flat baseline on silence)
            height = hasReal
              ? MIN_HEIGHT + Math.min(1, out[index] ?? 0) * (100 - MIN_HEIGHT)
              : MIN_HEIGHT;
          } else {
            // Fallback generic animation
            height = 20 + Math.sin(Date.now() / 200 + index * 0.5) * 15 + Math.random() * 10;
          }
          bar.style.height = `${height}%`;
        });
        animationRef.current = requestAnimationFrame(animate);
      };
      animationRef.current = requestAnimationFrame(animate);
    } else {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      barsRef.current.forEach((bar) => {
        if (bar) bar.style.height = `${MIN_HEIGHT}%`;
      });
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPlaying, getLevels]);

  return (
    <div className={cn("flex items-center justify-center gap-0.5 sm:gap-1.5 h-10 sm:h-16", className)}>
      {[...MAIN_BARS, ...EXTRA_BARS].map((bar, i) => (
        <div
          key={bar.id}
          ref={(el) => {
            if (el) barsRef.current[bar.index] = el;
          }}
          className={cn(
            "w-1 sm:w-1.5 rounded-full transition-all duration-100",
            i >= 20 && "hidden sm:block", // Show extra bars only on larger screens
            isPlaying ? "bg-exam-audio-wave" : "bg-muted-foreground/30"
          )}
          style={{ height: `${MIN_HEIGHT}%` }}
        />
      ))}
    </div>
  );
}
